import type { EventConfig } from '@/types'

export interface BoothOverrides {
  beautyFilter?: boolean
  beautyFilterStrength?: number
  watermark?: boolean
  greenScreen?: boolean
  digitalProps?: boolean
  poseSuggestions?: boolean
  poseSuggestionDelay?: number
  poseCount?: number
  countdownDelay?: number
  reviewTime?: number
  finishScreenTimeout?: number
  showPrintScreen?: boolean
  qrEnabled?: boolean
  galleryEnabled?: boolean
  virtualAttendant?: boolean
  resolution?: { width: number; height: number }
  frameRate?: number
  debugOverlayResolution?: boolean
  recordSessionVideo?: boolean
  maintenanceMode?: boolean
  maintenanceText?: string
  maintenanceTitle?: string
  advancedBeautyFilter?: boolean
  faceTrackingProp?: string
  startButtonImageEnabled?: boolean
  screensaverEnabled?: boolean
  maxMessages?: number
  intervalSeconds?: number
  motionSensitivity?: number
  wakeMethod?: 'motion' | 'touch'
  zigbeeLightEnabled?: boolean
  zigbeeLightDevice?: string
  zigbeeWsUrl?: string
}

/**
 * Returns a deep-merged copy of `event` with operator `overrides` applied on top.
 * Never mutates the original EventConfig object.
 */
export function mergeOverrides(event: EventConfig, overrides: BoothOverrides): EventConfig {
  return {
    ...event,
    capture: {
      ...event.capture,
      recordSessionVideo: overrides.recordSessionVideo !== undefined ? overrides.recordSessionVideo : event.capture.recordSessionVideo,
      finishScreenTimeout: overrides.finishScreenTimeout !== undefined ? overrides.finishScreenTimeout : event.capture.finishScreenTimeout,
      photo: {
        ...event.capture.photo,
        poseCount:
          overrides.poseCount !== undefined ? overrides.poseCount : event.capture.photo.poseCount,
        delayBeforeFirst:
          overrides.countdownDelay !== undefined
            ? overrides.countdownDelay
            : event.capture.photo.delayBeforeFirst,
        reviewTime:
          overrides.reviewTime !== undefined
            ? overrides.reviewTime
            : event.capture.photo.reviewTime,
      },
      poseSuggestions: {
        ...event.capture.poseSuggestions,
        enabled:
          overrides.poseSuggestions !== undefined
            ? overrides.poseSuggestions
            : (event.capture.poseSuggestions?.enabled ?? true),
        showSecondsBefore:
          overrides.poseSuggestionDelay !== undefined
            ? overrides.poseSuggestionDelay
            : (event.capture.poseSuggestions?.showSecondsBefore ?? 2),
      },
      attractLoop: {
        ...(event.capture.attractLoop || {}),
        enabled: overrides.screensaverEnabled !== undefined ? overrides.screensaverEnabled : (event.capture.attractLoop?.enabled ?? false),
        timeout: event.capture.attractLoop?.timeout ?? 60,
        maxMessages: overrides.maxMessages !== undefined ? overrides.maxMessages : event.capture.attractLoop?.maxMessages,
        intervalSeconds: overrides.intervalSeconds !== undefined ? overrides.intervalSeconds : event.capture.attractLoop?.intervalSeconds,
        motionSensitivity: overrides.motionSensitivity !== undefined ? overrides.motionSensitivity : event.capture.attractLoop?.motionSensitivity,
        wakeMethod: overrides.wakeMethod !== undefined ? overrides.wakeMethod : (event.capture.attractLoop?.wakeMethod ?? 'touch'),
      }
    },
    effects: {
      ...event.effects,
      digitalProps:
        overrides.digitalProps !== undefined ? overrides.digitalProps : event.effects.digitalProps,
      beautyFilter: {
        ...event.effects.beautyFilter,
        enabled:
          overrides.beautyFilter !== undefined
            ? overrides.beautyFilter
            : event.effects.beautyFilter.enabled,
        strength:
          overrides.beautyFilterStrength !== undefined
            ? overrides.beautyFilterStrength
            : event.effects.beautyFilter.strength,
      },
      watermark: {
        ...event.effects.watermark,
        enabled:
          overrides.watermark !== undefined ? overrides.watermark : event.effects.watermark.enabled,
      },
      greenScreen: {
        ...event.effects.greenScreen,
        enabled:
          overrides.greenScreen !== undefined
            ? overrides.greenScreen
            : event.effects.greenScreen.enabled,
      },
    },
    sharing: {
      ...event.sharing,
      qrEnabled:
        overrides.qrEnabled !== undefined ? overrides.qrEnabled : event.sharing.qrEnabled,
      galleryEnabled:
        overrides.galleryEnabled !== undefined
          ? overrides.galleryEnabled
          : event.sharing.galleryEnabled,
    },
    virtualAttendant: {
      ...event.virtualAttendant,
      enabled:
        overrides.virtualAttendant !== undefined
          ? overrides.virtualAttendant
          : event.virtualAttendant.enabled,
    },
    screens: {
      ...event.screens,
      welcome: {
        ...event.screens.welcome,
        startPrompt: overrides.startButtonImageEnabled === undefined
          ? event.screens.welcome.startPrompt
          : { ...event.screens.welcome.startPrompt, mode: overrides.startButtonImageEnabled ? (event.screens.welcome.startPrompt?.mode === 'custom' ? 'custom' : 'builtin') : 'off' },
        startButtonImage: {
          ...(event.screens.welcome.startButtonImage || { asset: null }),
          enabled: overrides.startButtonImageEnabled !== undefined ? overrides.startButtonImageEnabled : (event.screens.welcome.startButtonImage?.enabled ?? false)
        }
      }
    },
    camera: {
      ...event.camera,
      resolution: overrides.resolution !== undefined ? overrides.resolution : event.camera.resolution,
      frameRate: overrides.frameRate !== undefined ? overrides.frameRate : event.camera.frameRate,
    },
  }
}
